/**
 * RSI Reversal Strategy
 *
 * Plots RSI with overbought/oversold levels. Highlights reversal zones
 * when RSI exits extreme territory.
 *
 * Category: Advanced | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const rsiReversalIndicator: IndicatorDefinition = {
  id: "rsi_reversal",
  name: "RSI Reversal",
  shortName: "RSIRev",
  category: "advanced",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 5, max: 50 },
    { key: "overbought", label: "Overbought", type: "number", default: 70, min: 60, max: 90 },
    { key: "oversold", label: "Oversold", type: "number", default: 30, min: 10, max: 40 },
  ],
  plots: [
    { key: "rsi", label: "RSI", color: "#AB47BC", style: "line", lineWidth: 2 },
    { key: "ob", label: "Overbought", color: "#F44336", style: "line", lineWidth: 1 },
    { key: "os", label: "Oversold", color: "#4CAF50", style: "line", lineWidth: 1 },
    { key: "mid", label: "Mid", color: "#555555", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, overbought, oversold } = params;
    const closes = candles.map(c => c.close);

    const rsiData: { time: number; value: number }[] = [];
    const obData: { time: number; value: number }[] = [];
    const osData: { time: number; value: number }[] = [];
    const midData: { time: number; value: number }[] = [];

    let avgGain = 0, avgLoss = 0;

    for (let i = 1; i <= period && i < closes.length; i++) {
      const change = closes[i] - closes[i - 1];
      if (change > 0) avgGain += change;
      else avgLoss += Math.abs(change);
    }
    avgGain /= period;
    avgLoss /= period;

    if (period < closes.length) {
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - 100 / (1 + rs);
      rsiData.push({ time: candles[period].time, value: rsi });
      obData.push({ time: candles[period].time, value: overbought });
      osData.push({ time: candles[period].time, value: oversold });
      midData.push({ time: candles[period].time, value: 50 });
    }

    for (let i = period + 1; i < closes.length; i++) {
      const change = closes[i] - closes[i - 1];
      avgGain = (avgGain * (period - 1) + Math.max(change, 0)) / period;
      avgLoss = (avgLoss * (period - 1) + Math.max(-change, 0)) / period;
      const rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
      const rsi = 100 - 100 / (1 + rs);

      rsiData.push({ time: candles[i].time, value: rsi });
      obData.push({ time: candles[i].time, value: overbought });
      osData.push({ time: candles[i].time, value: oversold });
      midData.push({ time: candles[i].time, value: 50 });
    }

    return [
      { key: "rsi", data: rsiData },
      { key: "ob", data: obData },
      { key: "os", data: osData },
      { key: "mid", data: midData },
    ];
  },
};
